/**
 * Nosto recommendation carousel.
 *
 * Nosto injects its recommendation markup asynchronously (after page load, and
 * again on some re-renders such as variant changes), so we can't just query for
 * the elements once on DOMContentLoaded. Instead we watch the DOM for any
 * `.nosto-swiper` container that Nosto adds and initialise Swiper on it.
 *
 * The markup + scoped styles live in `nosto/product-carousel.html` (the Nosto
 * template). This file owns the carousel behaviour and the card add-to-cart handler.
 */

import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import { recommendationsEnabled } from './_nosto-flags.js';

const SELECTOR = '.nosto-swiper';

function initCarousel(el) {
  if (!el || el.dataset.swiperInitialized) return;
  el.dataset.swiperInitialized = 'true';

  // Arrows live on .rec-swiper (one level above .swiper) so they can straddle the
  // carousel edge without being clipped by .swiper's overflow:hidden — scope the
  // navigation lookup there, falling back to el if the wrapper isn't present.
  const scope = el.closest('.rec-swiper') || el;

  const swiper = new Swiper(el, {
    modules: [Navigation],
    slidesPerView: 2,
    spaceBetween: 40, // gap BETWEEN slides only (no outer-edge overhang); tweak to taste
    watchOverflow: true, // hide arrows / disable dragging when everything fits
    navigation: {
      prevEl: scope.querySelector('.swiper-button-prev'),
      nextEl: scope.querySelector('.swiper-button-next'),
    },
    breakpoints: {
      1024: { slidesPerView: 3 }, // matches the Rebuy block: 2 up to desktop, 3 above
    },
  });

  observeCardLoads(el, swiper);
}

/**
 * Slides now contain <nosto-dynamic-card> elements that fetch their real markup
 * asynchronously (a skeleton placeholder renders first). Swiper measures slide
 * heights at init — i.e. against the skeletons — so once the real cards inject we
 * must re-measure or the track height/arrows can be wrong.
 *
 * The dynamic-card web component doesn't expose a stable public "loaded" event, so
 * we watch the wrapper subtree for the injected content and debounce a
 * swiper.update() across the burst of mutations (all cards resolving at once).
 */
function observeCardLoads(el, swiper) {
  const wrapper = el.querySelector('.swiper-wrapper');
  if (!wrapper) return;

  let raf = null;
  const cardObserver = new MutationObserver(() => {
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => swiper.update());
  });

  cardObserver.observe(wrapper, { childList: true, subtree: true });
}

function initWithin(node) {
  if (node.nodeType !== 1) return;
  if (node.matches && node.matches(SELECTOR)) initCarousel(node);
  if (node.querySelectorAll) node.querySelectorAll(SELECTOR).forEach(initCarousel);
}

/**
 * Add-to-cart for the Nosto recommendation cards.
 *
 * The card visuals come from the theme via <nosto-dynamic-card>, but the button lives
 * in the Nosto template (carrying data-handle) so it can be styled and copy-controlled
 * from the template.
 *
 * Preferred path: forward the click to the theme card's own quick-add button. That runs
 * the theme's real add flow, which dispatches `variant:add` — the event
 * _nosto-attribution.js listens on — so attribution happens in exactly one place for
 * every Nosto surface, rather than this file self-attributing via addProductToCart. It
 * also means multi-variant products open the theme's size modal instead of silently
 * adding a default variant. The theme owns the button state and the cart refresh on this
 * path, so we don't touch either.
 *
 * Fallback path: `window.Nosto.addProductToCart(handle, btn, 1)`, which self-attributes
 * (Nosto walks `.closest()` from the passed element to resolve the recommendation slot).
 * This is NOT dead code — it's the live path whenever there's no theme button to forward
 * to:
 *   - the theme only renders quick-add under `show_quick_buy and product.available` and
 *     `market_oos == false` (snippets/product-card.liquid), so an out-of-stock or
 *     quick-buy-disabled card has no button and would otherwise do nothing at all;
 *   - the dynamic card may not have resolved yet when the click lands;
 *   - nosto/cart-drawer-suggestions.html hand-builds its compact rows with no
 *     <nosto-dynamic-card> and no .swiper-slide, so it always lands here — which is why
 *     the theme-button lookup is scoped to the slide, and why the "Adding…" state and the
 *     `cart:refresh` dispatch must stay on this branch.
 *
 * `cart:refresh` on documentElement is the same idiom the theme uses for programmatic
 * adds (see _wishlist_add_to_cart_fix.js); with cart_type "drawer" it refreshes the
 * drawer/header cart contents.
 *
 * Delegated on `document` because Nosto injects the cards asynchronously. Bound once.
 */
let addToCartBound = false;
function initAddToCart() {
  if (addToCartBound) return;
  addToCartBound = true;

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-nosto-add]');
    if (!btn) return;
    e.preventDefault();

    // Scoped to the slide so cart-drawer suggestions (no .swiper-slide) can't reach a
    // neighbouring card's button.
    const slide = btn.closest('.swiper-slide');
    const themeBtn =
      slide && slide.querySelector('nosto-dynamic-card product-card .product-card__quick-add-button');
    if (themeBtn) {
      themeBtn.click();
      return;
    }

    const handle = btn.dataset.handle;
    const add = window.Nosto && window.Nosto.addProductToCart;
    if (!handle || typeof add !== 'function') {
      if (typeof add !== 'function') console.warn('[nosto] addProductToCart unavailable');
      return;
    }
    if (btn.dataset.adding) return; // guard against double-clicks

    const label = btn.textContent;
    btn.dataset.adding = 'true';
    btn.disabled = true;
    btn.textContent = 'Adding…';

    try {
      // Nosto Shopify helper: (productHandle, element, quantity). Returns a Promise.
      await add(handle, btn, 1);
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (err) {
      console.error('[nosto] add to cart failed', err);
    } finally {
      btn.disabled = false;
      btn.textContent = label;
      delete btn.dataset.adding;
    }
  });
}

function start() {
  initAddToCart();

  // Catch anything already in the DOM (e.g. a fast Nosto render).
  document.querySelectorAll(SELECTOR).forEach(initCarousel);

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach(initWithin);
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

// Off unless the "Nosto recommendations" theme setting is on. The module stays in the
// bundle either way, so the setting alone flips it — no rebuild. See _nosto-flags.js.
if (recommendationsEnabled()) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
}
